import { useEffect, useMemo } from "react";

import {
  Notification,
  NotificationType,
} from "@transfr-inc/dashboard-components";
import {
  Dropdown,
  DropdownButton,
  DropdownItem,
  DropdownItems,
} from "@transfr-inc/dashboard-components/forms";

import {
  useAiCurriculumSourceNames,
  useAiCurriculumSubjects,
} from "@queries/ai-curriculum";

import "./ai-state-select.step.scss";

const toOptions = (names) => (names ?? []).map((name) => ({ id: name, name }));

// Only California is offered at this time. The `id` must match the value the
// backend expects for the subjects/source-names lookups (the state name).
const AVAILABLE_STATES = [{ id: "California", name: "California" }];

// Sentinel option for the optional sourceName drill-down. When the user
// picks this, the modal sends the standards-load request without a
// sourceName field, which the backend treats as "include every standard
// under the chosen subject."
const ALL_WITHIN_SUBJECT_OPTION = {
  id: "__all_within_subject__",
  name: "All within subject",
};

export const isAllWithinSubjectOption = (option) =>
  option?.id === ALL_WITHIN_SUBJECT_OPTION.id;

export const AiStateSelectStep = ({
  selectedState,
  selectedSubject,
  selectedSourceName,
  onSelectedStateChange,
  onSelectedSubjectChange,
  onSelectedSourceNameChange,
  error,
  onError,
}) => {
  const {
    data: subjectsResponse,
    isFetching: loadingSubjects,
    isError: subjectsError,
  } = useAiCurriculumSubjects(selectedState?.id);

  const {
    data: sourceNamesResponse,
    isFetching: loadingSourceNames,
    isError: sourceNamesError,
  } = useAiCurriculumSourceNames(selectedState?.id, selectedSubject?.id);

  const states = AVAILABLE_STATES;
  const subjects = useMemo(
    () => toOptions(subjectsResponse?.subjects),
    [subjectsResponse]
  );
  // Always offer the "All within subject" choice at the top of the list,
  // regardless of how many specific drill-downs exist.
  const sourceNames = useMemo(
    () => [
      ALL_WITHIN_SUBJECT_OPTION,
      ...toOptions(sourceNamesResponse?.sourceNames),
    ],
    [sourceNamesResponse]
  );

  useEffect(() => {
    if (subjectsError) onError("We couldn't load subjects for that state.");
  }, [subjectsError, onError]);

  useEffect(() => {
    if (sourceNamesError)
      onError("We couldn't load frameworks for that subject.");
  }, [sourceNamesError, onError]);

  return (
    <div className="ai-state-select-step">
      {error && (
        <Notification type={NotificationType.error}>{error}</Notification>
      )}

      <header className="ai-state-select-step__header">
        <h2>Select your state, subject, and framework</h2>
        <p>
          We'll match your course to the official standards published by your
          state. Pick a framework to narrow the match, or leave it on{" "}
          <em>All within subject</em> to include everything under the chosen
          subject.
        </p>
      </header>

      <div className="ai-state-select-step__fields">
        <div className="ai-state-select-step__field">
          <span className="ai-state-select-step__label">State</span>
          <Dropdown
            selected={selectedState}
            onOptionSelected={onSelectedStateChange}
          >
            <DropdownButton placeholder="Select a state" required clearEnabled>
              {selectedState?.name}
            </DropdownButton>
            <DropdownItems minHeight={200}>
              {states.map((s) => (
                <DropdownItem key={s.id} value={s}>
                  {s.name}
                </DropdownItem>
              ))}
            </DropdownItems>
          </Dropdown>
        </div>

        <div className="ai-state-select-step__field">
          <span className="ai-state-select-step__label">Subject</span>
          <Dropdown
            selected={selectedSubject}
            onOptionSelected={onSelectedSubjectChange}
            disabled={!selectedState || loadingSubjects}
          >
            <DropdownButton
              placeholder={
                !selectedState
                  ? "Select a state first"
                  : loadingSubjects
                  ? "Loading subjects…"
                  : "Select a subject"
              }
              required
              clearEnabled
            >
              {selectedSubject?.name}
            </DropdownButton>
            <DropdownItems minHeight={200}>
              {subjects.map((s) => (
                <DropdownItem key={s.id} value={s}>
                  {s.name}
                </DropdownItem>
              ))}
            </DropdownItems>
          </Dropdown>
        </div>

        <div className="ai-state-select-step__field">
          <span className="ai-state-select-step__label">Framework</span>
          <Dropdown
            selected={selectedSourceName}
            onOptionSelected={onSelectedSourceNameChange}
            disabled={!selectedSubject || loadingSourceNames}
          >
            <DropdownButton
              placeholder={
                !selectedSubject
                  ? "Select a subject first"
                  : loadingSourceNames
                  ? "Loading frameworks…"
                  : "All within subject"
              }
              clearEnabled
            >
              {selectedSourceName?.name}
            </DropdownButton>
            <DropdownItems minHeight={200}>
              {sourceNames.map((sn) => (
                <DropdownItem key={sn.id} value={sn}>
                  {sn.name}
                </DropdownItem>
              ))}
            </DropdownItems>
          </Dropdown>
        </div>
      </div>
    </div>
  );
};
